import { CheckCircle2, Circle, Clock, Ban, Send, FilePlus, Wallet, AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "./Badge";

/*
 * Centralised label + tone + icon for every workflow status, so the visual
 * treatment stays consistent. Accepts any string and falls back to a neutral
 * badge for unknown statuses.
 */
type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

interface Preset {
  label: string;
  tone: Tone;
  icon?: ReactNode;
}

const STATUS_PRESETS: Record<string, Preset> = {
  // Contribution lifecycle (submitted → allocated → confirmed → settled)
  submitted:   { label: "Submitted",   tone: "info",    icon: <FilePlus /> },
  allocated:   { label: "Allocated",   tone: "info",    icon: <CheckCircle2 /> },
  confirmed:   { label: "Confirmed",   tone: "success", icon: <CheckCircle2 /> },
  settled:     { label: "Settled",     tone: "success", icon: <Wallet /> },
  reversed:    { label: "Reversed",    tone: "danger",  icon: <Ban /> },
  void:        { label: "Void",        tone: "danger",  icon: <Ban /> },
  unallocated: { label: "Unallocated", tone: "warning", icon: <Clock /> },
  pending:     { label: "Pending",     tone: "warning", icon: <Clock /> },
  // Generic lifecycle
  active:      { label: "Active",      tone: "success", icon: <CheckCircle2 /> },
  inactive:    { label: "Inactive",    tone: "neutral", icon: <Circle /> },
  overdue:     { label: "Overdue",     tone: "danger",  icon: <AlertTriangle /> },
  sent:        { label: "Sent",        tone: "info",    icon: <Send /> },
};

interface StatusBadgeProps {
  status: string;
  hideIcon?: boolean;
  className?: string;
}

export function StatusBadge({ status, hideIcon, className }: StatusBadgeProps) {
  const preset = STATUS_PRESETS[status];
  if (!preset) {
    return (
      <Badge tone="neutral" className={className}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : "Unknown"}
      </Badge>
    );
  }
  return (
    <Badge tone={preset.tone} icon={hideIcon ? undefined : preset.icon} className={className}>
      {preset.label}
    </Badge>
  );
}
